/**
 * Filename helpers for the "Duplicate file detected" flow: when an upload's
 * name collides with a document already in the same library, "Keep both"
 * saves the new file under the next free `Name (n).ext`.
 */

const COPY_SUFFIX = /^(.*?) \((\d+)\)$/

export function splitFileName(fileName: string): {
  base: string
  extension: string
} {
  const dot = fileName.lastIndexOf('.')
  // Leading-dot names (".env") and extensionless names keep everything in base.
  if (dot <= 0 || dot === fileName.length - 1) {
    return { base: fileName, extension: '' }
  }
  return { base: fileName.slice(0, dot), extension: fileName.slice(dot) }
}

/** `Contract (2)` → `Contract`, so re-uploading a copy doesn't yield `(2) (2)`. */
export function stripCopySuffix(base: string): string {
  const match = COPY_SUFFIX.exec(base)
  return match?.[1] ? match[1] : base
}

export function isSameFileName(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase()
}

/**
 * Returns `fileName` when no taken name matches it (case-insensitive),
 * otherwise the lowest `Base (n).ext` with n ≥ 2 that is free.
 */
export function nextAvailableFileName(
  fileName: string,
  takenNames: Iterable<string>,
): string {
  const taken = new Set<string>()
  for (const name of takenNames) taken.add(name.trim().toLowerCase())

  if (!taken.has(fileName.trim().toLowerCase())) return fileName

  const { base, extension } = splitFileName(fileName)
  const root = stripCopySuffix(base)

  for (let n = 2; ; n += 1) {
    const candidate = `${root} (${n})${extension}`
    if (!taken.has(candidate.toLowerCase())) return candidate
  }
}
